import { useTranslation } from 'react-i18next';

/**
 * One status vocabulary for the whole app (redesign, GLOSSARY-FR §2). A badge
 * takes a `Status`, never a colour: the tone and the word come together, so
 * colour is never the only signal. Cancelled is not Failed: outline, no colour.
 * Stopped is a campaign's cancelled (D-197): the same look, the word of its
 * "Arrêter la campagne" (D-192); its posts stay Cancelled.
 */
export type Status =
  | 'draft'
  | 'active'
  | 'paused'
  | 'scheduled'
  | 'published'
  | 'failed'
  | 'cancelled'
  | 'stopped'
  | 'completed'
  | 'noTime'
  | 'unpublishedChanges'
  | 'atLimit'
  // A channel account (R36): "Connecté", and the states a Meta token can fall into.
  | 'connected'
  | 'expired'
  | 'error'
  | 'disconnected';

export const STATUSES: readonly Status[] = [
  'draft',
  'active',
  'paused',
  'scheduled',
  'published',
  'failed',
  'cancelled',
  'stopped',
  'completed',
  'noTime',
  'unpublishedChanges',
  'atLimit',
  'connected',
  'expired',
  'error',
  'disconnected',
];

const NEUTRAL = 'bg-surface-3 text-ink-2';
const OUTLINE = 'bg-transparent text-ink-2 ring-1 ring-inset ring-border-strong';
const WARNING = 'bg-warning-50 text-warning-700';

const TONES: Record<Status, { tone: string; dot: boolean }> = {
  draft: { tone: NEUTRAL, dot: true },
  active: { tone: 'bg-success-50 text-success-700', dot: true },
  paused: { tone: NEUTRAL, dot: true },
  scheduled: { tone: 'bg-info-50 text-info-700', dot: true },
  published: { tone: 'bg-primary-50 text-primary-700', dot: true },
  failed: { tone: 'bg-danger-50 text-danger-700', dot: true },
  cancelled: { tone: OUTLINE, dot: false },
  stopped: { tone: OUTLINE, dot: false },
  completed: { tone: 'bg-success-50 text-success-700', dot: true },
  noTime: { tone: WARNING, dot: true },
  unpublishedChanges: { tone: WARNING, dot: true },
  atLimit: { tone: WARNING, dot: true },
  connected: { tone: 'bg-success-50 text-success-700', dot: true },
  expired: { tone: WARNING, dot: true },
  error: { tone: 'bg-danger-50 text-danger-700', dot: true },
  disconnected: { tone: NEUTRAL, dot: true },
};

export interface StatusBadgeProps {
  status: Status;
  /** Extra words after the label, e.g. the date of a scheduled post ("Programmée · 15 mars 2027"). */
  detail?: string;
  className?: string;
}

export function StatusBadge({ status, detail, className = '' }: StatusBadgeProps) {
  const { t } = useTranslation();
  const { tone, dot } = TONES[status];
  return (
    <span
      className={`inline-flex h-[22px] shrink-0 items-center gap-[5px] whitespace-nowrap rounded-full px-2 text-ds-xs font-semibold ${tone} ${className}`}
    >
      {dot ? <span aria-hidden className="size-1.5 rounded-full bg-current" /> : null}
      {t(`status.${status}`)}
      {detail ? ` · ${detail}` : null}
    </span>
  );
}
